import axios from "axios";
import { authApiEndpoint } from "../configuration";

import cookiesService from "./CookiesServices";
import {
  getAccessContext,
  getNumericRoleIdFromSession,
  ROLE_SUPER_ADMIN,
  ROLE_VENDOR_USER,
} from "../utils/accessControl";

// Default axios config for authenticated calls
let axiosConfig = {
  withCredentials: false,
};

class AuthService {
  constructor() {
    const token = cookiesService.get("token");
    this.authenticated = !!token;
  }

  /**
   * Reads the logged-in user from cookies and returns it as an object.
   * The backend typically stores `user` as a JSON string.
   */
  getParsedUser() {
    const raw = cookiesService.get("user");
    if (!raw) return null;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        try {
          // Some browsers/storage URL-encode cookie values.
          // Only decode if it looks URL-encoded to avoid URI errors.
          const decoded = raw.includes("%") ? decodeURIComponent(raw) : raw;
          return JSON.parse(decoded);
        } catch {
          return null;
        }
      }
    }
    if (typeof raw === "object") return raw;
    return null;
  }

  getCurrentRoleId() {
    const n = getNumericRoleIdFromSession();
    return n === null ? null : String(n);
  }

  getCurrentVendorId() {
    const { vendorId } = getAccessContext();
    return vendorId;
  }

  isSuperAdmin() {
    const { roleId } = getAccessContext();
    return roleId === ROLE_SUPER_ADMIN;
  }

  isVendorUser() {
    const { roleId } = getAccessContext();
    return roleId === ROLE_VENDOR_USER;
  }

  async ValidateUser(data) {
    try {
      // Clear stale auth cookies so backend gets a clean login
      cookiesService.remove("token");
      cookiesService.remove("user");

      // IMPORTANT: use the API host from configuration, and let axios add only standard headers
      // Avoid custom headers like Cache-Control/Pragma which the API CORS config does not allow.
      const response = await axios.post(
        `${authApiEndpoint}/adminusers/verify`,
        data,
        axiosConfig
      );

      if (process.env.NODE_ENV === "development") {
        console.log(
          "ValidateUser response",
          response?.status,
          response?.data?.status
        );
      }

      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error(
          "ValidateUser error",
          error?.response?.status,
          error?.response?.data,
          error?.message
        );
      }
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Network or server error. Try again or refresh the page.";
      return { status: false, message };
    }
  }

  async UpdateUserToken(token) {
    console.log("In API Token in UpdateUserToken", token);
    try {
      const response = await axios.post(
        `${authApiEndpoint}/adminusers/updatetoken`,
        token,
        axiosConfig
      );
      console.log("response", response);
      return response.data;
    } catch (error) {
      console.error("error", error);
      return false;
    }
  }

  isAuthenticated() {
    const token = cookiesService.get("token");
    console.log("isAuthenticated", token);
    this.authenticated = !!token;
    return this.authenticated;
  }
  isAuthorised(requiredRoles) {
    // If a route/page doesn't specify role requirements, allow access.
    if (requiredRoles === undefined || requiredRoles === null) return true;

    // If user isn't present in cookies, treat as not authorized.
    const currentRoleId = this.getCurrentRoleId();
    if (!currentRoleId) return false;

    const requiredList = Array.isArray(requiredRoles)
      ? requiredRoles
      : [requiredRoles];

    return requiredList.some((r) => {
      if (r === undefined || r === null) return false;
      return String(r) === currentRoleId;
    });
  }
  getUserDetails() {
    let data = cookiesService.get("user");
    console.log("getUserDetails", data);
    if (data) {
      return data;
    } else {
      return [];
    }
  }
  handleLoginSuccess(response, remember) {
    let user = null;
    if (Array.isArray(response) && response[0]) {
      user = response[0];
    } else if (response && typeof response === "object" && response.token) {
      user = response;
    }
    if (!user || !user.token) {
      if (process.env.NODE_ENV === "development") {
        console.error("handleLoginSuccess: invalid response or missing token", response);
      }
      return false;
    }
    const token = user.token;
    if (!token) {
      if (process.env.NODE_ENV === "development") {
        console.error("handleLoginSuccess: no token in response", user);
      }
      return false;
    }
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + 24 * 60 * 60 * 1000); // 24hrs

    const cookieBase = {
      path: "/",
      expires: expiryDate,
      sameSite: "lax",
      ...(typeof window !== "undefined" &&
        window.location.protocol === "https:" && { secure: true }),
    };
    cookiesService.set("token", token, cookieBase);
    cookiesService.set("user", JSON.stringify(user), cookieBase);
    return true;
  }
  async logout() {
    try {
      cookiesService.remove("token");
      cookiesService.remove("user");
      cookiesService.remove("data");
      const response = await axios.post(
        `${authApiEndpoint}/adminusers/logout`,
        {},
        axiosConfig
      );
      console.log("response", response);
      return response.data;
    } catch (error) {
      console.error("error", error);
      return false;
    }
  }
}
const authService = new AuthService();
export default authService;
